/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-09-07: Split-screen persistence utilities for Phase 4
 */

import {
  SplitConfig,
  DEFAULT_SPLIT_CONFIG,
  SPLIT_CONSTRAINTS,
  SPLIT_CONFIG_STORAGE_KEY,
} from '../constants/splitScreen';

/**
 * Validates that a split configuration meets all constraints.
 * Returns error message if invalid, null if valid.
 */
export function validateSplitConfig(config: SplitConfig): string | null {
  const { leftWidth, middleWidth } = config;
  const rightWidth = 100 - leftWidth - middleWidth;

  // Check individual constraints
  if (leftWidth < SPLIT_CONSTRAINTS.leftMin) {
    return `Left section must be at least ${SPLIT_CONSTRAINTS.leftMin}%`;
  }
  if (leftWidth > SPLIT_CONSTRAINTS.leftMax) {
    return `Left section cannot exceed ${SPLIT_CONSTRAINTS.leftMax}%`;
  }

  if (middleWidth < SPLIT_CONSTRAINTS.middleMin) {
    return `Middle section must be at least ${SPLIT_CONSTRAINTS.middleMin}%`;
  }
  if (middleWidth > SPLIT_CONSTRAINTS.middleMax) {
    return `Middle section cannot exceed ${SPLIT_CONSTRAINTS.middleMax}%`;
  }

  if (rightWidth < SPLIT_CONSTRAINTS.rightMin) {
    return `Right section must be at least ${SPLIT_CONSTRAINTS.rightMin}%`;
  }

  // Check total
  const total = leftWidth + middleWidth + rightWidth;
  if (Math.abs(total - 100) > 0.01) {
    // Allow tiny floating point errors
    return `Widths must sum to 100% (currently ${total}%)`;
  }

  return null; // Valid
}

/**
 * Attempts to adjust widths to meet constraints when user changes one value.
 * Returns adjusted config or null if adjustment is impossible.
 */
export function adjustToConstraints(
  leftWidth: number,
  middleWidth: number,
  changedSection: 'left' | 'middle'
): { left: number; middle: number } | null {
  const rightWidth = 100 - leftWidth - middleWidth;

  // If valid, return as-is
  if (
    leftWidth >= SPLIT_CONSTRAINTS.leftMin &&
    leftWidth <= SPLIT_CONSTRAINTS.leftMax &&
    middleWidth >= SPLIT_CONSTRAINTS.middleMin &&
    middleWidth <= SPLIT_CONSTRAINTS.middleMax &&
    rightWidth >= SPLIT_CONSTRAINTS.rightMin
  ) {
    return { left: leftWidth, middle: middleWidth };
  }

  // If right too small, reduce the changed section
  if (rightWidth < SPLIT_CONSTRAINTS.rightMin) {
    const needed = SPLIT_CONSTRAINTS.rightMin - rightWidth;

    if (changedSection === 'left') {
      // Reduce left first
      const newLeft = leftWidth - needed;
      if (newLeft >= SPLIT_CONSTRAINTS.leftMin) {
        return { left: newLeft, middle: middleWidth };
      }
      // If can't reduce left enough, also reduce middle
      const leftReduction = leftWidth - SPLIT_CONSTRAINTS.leftMin;
      const middleReduction = needed - leftReduction;
      const newMiddle = middleWidth - middleReduction;
      if (newMiddle >= SPLIT_CONSTRAINTS.middleMin) {
        return { left: SPLIT_CONSTRAINTS.leftMin, middle: newMiddle };
      }
    } else {
      // changedSection === 'middle'
      const newMiddle = middleWidth - needed;
      if (newMiddle >= SPLIT_CONSTRAINTS.middleMin) {
        return { left: leftWidth, middle: newMiddle };
      }
      // If can't reduce middle enough, also reduce left
      const middleReduction = middleWidth - SPLIT_CONSTRAINTS.middleMin;
      const leftReduction = needed - middleReduction;
      const newLeft = leftWidth - leftReduction;
      if (newLeft >= SPLIT_CONSTRAINTS.leftMin) {
        return { left: newLeft, middle: SPLIT_CONSTRAINTS.middleMin };
      }
    }

    return null; // Can't satisfy constraints
  }

  return { left: leftWidth, middle: middleWidth };
}

/**
 * Load split config from localStorage
 */
export function loadSplitConfig(): SplitConfig {
  try {
    const stored = localStorage.getItem(SPLIT_CONFIG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as SplitConfig;

      // Validate loaded config
      const error = validateSplitConfig(parsed);
      if (error) {
        console.warn('Invalid split config in localStorage, using defaults:', error);
        return DEFAULT_SPLIT_CONFIG;
      }

      return parsed;
    }
  } catch (err) {
    console.error('Failed to load split config from localStorage:', err);
  }

  return DEFAULT_SPLIT_CONFIG;
}

/**
 * Save split config to localStorage
 */
export function saveSplitConfig(config: SplitConfig): void {
  try {
    localStorage.setItem(SPLIT_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch (err) {
    console.error('Failed to save split config to localStorage:', err);
  }
}

/**
 * Clear split config from localStorage (useful for debugging/reset)
 */
export function clearSplitConfig(): void {
  try {
    localStorage.removeItem(SPLIT_CONFIG_STORAGE_KEY);
  } catch (err) {
    console.error('Failed to clear split config from localStorage:', err);
  }
}

/**
 * Get right section width (derived value)
 */
export function getRightWidth(config: SplitConfig): number {
  return 100 - config.leftWidth - config.middleWidth;
}

/**
 * Get section widths in pixels for given viewport
 */
export function getSectionWidths(
  config: SplitConfig,
  viewportWidth: number
): {
  leftPx: number;
  middlePx: number;
  rightPx: number;
} {
  const leftPx = (config.leftWidth / 100) * viewportWidth;
  const middlePx = (config.middleWidth / 100) * viewportWidth;
  const rightPx = viewportWidth - leftPx - middlePx;

  return { leftPx, middlePx, rightPx };
}

/**
 * Check if split can be activated (both frames selected)
 */
export function canActivateSplit(config: SplitConfig): boolean {
  return config.leftFrameId !== null && config.rightFrameId !== null;
}
